import { Component,EventEmitter,Output,Input,OnChanges,ViewChild,SimpleChanges} from '@angular/core';
import { DxAccordionComponent } from 'devextreme-angular';
import { FormSectionService } from '../form-section.service';
import { ValidationService } from '../validation.service';
import { Keys,Labels,Quantities } from '../gasflow.constants';
import { UnitsService, Quantity, FormattingService, Pipe, Gas } from 'pumpcalc-core';
import { GasflowBoundaryConditions,GasflowSolverService} from '../gasflow-solver.service';
import { StatusBarService } from 'projects/pumpcalc-core/src/public-api';

@Component({
  selector: 'parameters',
  templateUrl: './parameters.component.html',
  styleUrls: ['./parameters.component.css']
})
export class ParametersComponent implements OnChanges {

  @ViewChild("accordion", { static: true }) accordion: DxAccordionComponent;
  @Output() calculationResult: EventEmitter<any> = new EventEmitter();
  @Output() editing: EventEmitter<boolean> = new EventEmitter();
  @Output() parametersChange: EventEmitter<{pipes:Pipe[],conditions:any,gas:Gas,rangeVariables:any}> = new EventEmitter();
  @Input() gas:Gas = new Gas();
  pipes:Pipe[]=[new Pipe("1")];
  pipesForRangeVariables=this.pipes.slice(0);
  conditions = {
    [Keys.systemDownstreamPressure]:{input: true,   quantity: Quantity.PRESSURE,  value: 0},
    [Keys.systemUpstreamPressure]:{input: true, quantity:  Quantity.PRESSURE, value: 0}
  };
  gasTemperature:number=293.15;
  gasError:string;
  errors={};
  keys=Keys;
  calculateAttempted=false;
  gasflowBoundaryConditions:GasflowBoundaryConditions;
  calculating=false;
  calculationStatus= this.statusBarService.clear();
  calculationsCount=1;
  rangeVariables={};
  isEditing=true;
  solverError;
  pipeChangeSinceLastEdit=false;

  sections = [
    {template:Keys.pipeWorkSection, title:Labels[Keys.pipeWorkSection],summary:"",disabled:false},
    {template:Keys.gasSection,title:Labels[Keys.gasSection],summary:"",disabled:false},
    {template:Keys.boundaryConditionsSection,title:Labels[Keys.boundaryConditionsSection],summary:"",disabled:false},
    {template:Keys.rangeOnVariableSection,title:Labels[Keys.rangeOnVariableSection],summary:"",disabled:false}
  ];

  constructor(
    private formSectionService:FormSectionService,
    private validationService:ValidationService,
    private unitsService:UnitsService,
    private gasflowSolverService:GasflowSolverService,
    private statusBarService:StatusBarService,
    public  formattingService: FormattingService
    ) {
      this.unitsService.unitChangeAnnounced.subscribe((change=>{
        this.setBoundariesSectionSummary();
        this.setPipeworkSectionSummary();
        this.setGasSectionSummary();
        this.setRangeVariableSummary();
      }))
    }

  ngOnChanges(changes: SimpleChanges): void {
    this.setGas({gas:this.gas,temperature:this.gasTemperature});
  }

  getPendingUpdatesAndCalculate(){
    this.calculationStatus = this.statusBarService.statusLoading("Checking");
    this.calculating=true;
    let interval= setInterval(function () {
      if (this.pipeChangeSinceLastEdit) {
        this.calculate();
        clearInterval(interval);
      }
    }.bind(this), 500);
  }

  calculate(){
    this.solverError=null;
    this.calculateAttempted=true;
    if(!this.validate().valid) {
      this.calculating=false;
      this.calculationStatus = this.statusBarService.clear();
      return;
    }
    this.closeSections();
    this.calculationStatus = this.statusBarService.statusLoading("Calculating");
    this.calculating=true;
    this.disableSections(true);
    console.log(this.gasflowSolverService.createCalculationParameters(this.pipes,this.gas,this.gasTemperature,this.gasflowBoundaryConditions,this.rangeVariables));
    this.gasflowSolverService.calculate(this.gasflowSolverService.createCalculationParameters(this.pipes,this.gas,this.gasTemperature,this.gasflowBoundaryConditions,this.rangeVariables)).subscribe(
      solverResult =>{
        this.disableSections(false);
        this.calculationStatus = this.statusBarService.clear();
        this.calculating=false;
        let solverResultValidation = this.solverResultValidation(solverResult);
        if(!solverResultValidation.valid && solverResult.results.length==1){this.solverError=solverResultValidation.error;return}
        this.calculationsCount++;
        this.isEditing=false;
        this.editing.emit(this.isEditing);
        this.calculationResult.emit(solverResult);
      },
      error => {
        this.calculating=false;
        if(error != "User Cancelled" ) {
          this.calculationStatus = this.statusBarService.clear();
          this.disableSections(false);
          return;
        }
        this.calculationStatus = this.statusBarService.statusWarning("There was problem processing that calculation. Please try again later or contact support.");
      }
    )
  }

  validate():any{
    let validationResult = this.validationService.validateCalculation(this.pipes,this.gas,this.gasTemperature,this.conditions,this.rangeVariables);
    this.errors = validationResult.errors;
    this.formSectionService.setErrors(Labels[Keys.pipeWorkSection],this.errors[Keys.pipeWorkSection],this.sections);
    this.formSectionService.setErrors(Labels[Keys.gasSection],this.errors[Keys.gasSection],this.sections);
    this.formSectionService.setErrors(Labels[Keys.boundaryConditionsSection],this.errors[Keys.boundaryConditionsSection],this.sections);
    this.formSectionService.setErrors(Labels[Keys.rangeOnVariableSection],this.errors[Keys.rangeOnVariableSection],this.sections);
    return validationResult;
  }

  setGas(gasSelection:{gas:Gas,temperature:number}){
    this.gas = gasSelection.gas;
    this.gasTemperature = gasSelection.temperature;
    this.setGasSectionSummary();
    if(this.calculateAttempted){this.validate();}
    this.announceChange();
  }

  setGasSectionSummary(){
    this.formSectionService.setSummary(Labels[Keys.gasSection], this.gas.name + " " + this.formattingService.formatNumber(this.unitsService.convertFromSIForQuantity(Quantity.TEMPERATURE,this.gasTemperature)) + " " + this.unitsService.getSelectedUnitForQuantity(Quantity.TEMPERATURE), this.sections);
  }

  setPipeworkSectionSummary(){
    this.pipeChangeSinceLastEdit=true;
    this.pipesForRangeVariables=this.clone(this.pipes);
    if(this.calculateAttempted){this.validate();}
    if(this.pipes.length > 1) {
      let length = 0;
      this.pipes.forEach(pipe=>{length+=pipe.length})
      this.formSectionService.setSummary(Labels[Keys.pipeWorkSection], this.pipes.length + " Sections " + this.formattingService.formatNumber(this.unitsService.convertFromSIForQuantity(Quantity.LENGTH,length)) + " " + this.unitsService.getSelectedUnitForQuantity(Quantity.LENGTH),this.sections);
      this.announceChange();
      return;
    }
    this.formSectionService.setSummary(Labels[Keys.pipeWorkSection],
    'x' + this.pipes[0].parallel
    + ' ' + this.pipes[0].section
    + ' ' + (this.pipes[0].section == Labels[Keys.circular] ?
    + this.formattingService.formatNumber(this.unitsService.convertFromSIForQuantity(Quantity.XDIM,this.pipes[0].xdim1)) + this.unitsService.getSelectedUnitForQuantity(Quantity.XDIM)
    + ' x ' +  this.formattingService.formatNumber(this.unitsService.convertFromSIForQuantity(Quantity.LENGTH,this.pipes[0].length)) + this.unitsService.getSelectedUnitForQuantity(Quantity.LENGTH)
    :  this.formattingService.formatNumber(this.unitsService.convertFromSIForQuantity(Quantity.XDIM,this.pipes[0].xdim1)) + " " + this.unitsService.getSelectedUnitForQuantity(Quantity.XDIM)  + ' x '
    + this.formattingService.formatNumber(this.unitsService.convertFromSIForQuantity(Quantity.XDIM,this.pipes[0].xdim2)) + " " + this.unitsService.getSelectedUnitForQuantity(Quantity.XDIM) + ' x '
    + this.pipes[0].length + " " + this.unitsService.getSelectedUnitForQuantity(Quantity.LENGTH)),this.sections);
    this.announceChange();
  }

  setBoundaries(event:{conditions:any,gasflowBoundaryConditions:GasflowBoundaryConditions}){
    this.gasflowBoundaryConditions = event.gasflowBoundaryConditions;
    this.conditions = event.conditions;
    this.setBoundariesSectionSummary();
    if(this.calculateAttempted){this.validate();}
    this.announceChange();
  }

  setBoundariesSectionSummary(){
    let boundarySummary = "";
    Object.keys(this.conditions).forEach(key => {
      boundarySummary += Labels[key] + "=" + this.formattingService.formatNumber(this.unitsService.convertFromSIForQuantity(this.conditions[key].quantity,this.conditions[key].value)) + " " + this.unitsService.getSelectedUnitForQuantity(this.conditions[key].quantity) + ", "
    })
    this.formSectionService.setSummary("Boundary Conditions",boundarySummary,this.sections);
  }

  announceChange(){
    this.parametersChange.emit({pipes:this.pipes,conditions:this.conditions,gas:this.gas,rangeVariables:this.rangeVariables});
  }

  closeSections() {
    if(!this.accordion.instance){return;}
    for (let i = 0; i < this.sections.length; i++) {
      this.accordion.instance.collapseItem(i);
    }
  }

  cancelCalculation(){
    this.gasflowSolverService.stopCalculating();
  }

  rangeVariableChange(change){
    this.rangeVariables=change;
    this.setRangeVariableSummary();
    this.announceChange();
  }

  setRangeVariableSummary(){
    if(!this.isDefined(this.rangeVariables[1])){
      this.formSectionService.setSummary(Labels[Keys.rangeOnVariableSection],"",this.sections);
      return;
    }
    let param =  (this.rangeVariables[1].pipe?"Pipe "+this.rangeVariables[1].pipe+" ":"") + Labels[this.rangeVariables[1].key];
    let min = Quantities[this.rangeVariables[1].key]? this.formattingService.formatNumber(this.unitsService.convertFromSIForQuantity(this.rangeVariables[1].quantity,this.rangeVariables[1].min)) : this.rangeVariables[1].min;
    let max = Quantities[this.rangeVariables[1].key]? this.formattingService.formatNumber(this.unitsService.convertFromSIForQuantity(this.rangeVariables[1].quantity,this.rangeVariables[1].max)) : this.rangeVariables[1].max;
    let units = this.unitsService.getSelectedUnitForQuantity(this.rangeVariables[1].quantity);
    this.formSectionService.setSummary(Labels[Keys.rangeOnVariableSection],param + " " + min + " to " + max + " " + units,this.sections);
  }

  edit(){
    this.isEditing=true;
    this.editing.emit(this.isEditing);
  }

  isDefined(variable):boolean {
    return typeof(variable) != "undefined"
  }

  disableSections(state: boolean) {
    for (let i = 0; i < this.sections.length; i++) {
      if (this.accordion.instance) {
        this.accordion.instance.collapseItem(i);
      }
      this.sections[i].disabled = state;
    }
  }

  solverResultValidation(solverResult):{valid:boolean,error:string}{
    let valid = true;
    let error = "";
    solverResult.results.forEach(result => {
      if(result.calculationFailed){
        valid=false;
        error=result.calculationMessage;
        return;
      }
    });
    return {valid:valid,error:error}
  }

  setEditingPipeWork(state:boolean){
    this.pipeChangeSinceLastEdit=!state;
  }

  clone(object:any):any{
    return JSON.parse(JSON.stringify(object));
  }

}

//todo fix broken gf unit tests
